/**
 * Capture of the browser's install prompt, registered at application START.
 *
 * THE BUG THIS FIXES. `beforeinstallprompt` fires once, very early, and a
 * listener registered after it has fired never hears it - the browser does not
 * replay the event. The listener used to live inside hooks/usePWAInstall.js,
 * which only executes when the chunk containing the profile menu runs. Chrome
 * routinely fires the event before that, so the prompt was lost and the Install
 * App button sat permanently disabled on an app that was perfectly installable.
 *
 * This module is imported first thing in index.jsx, before React, so the
 * listener exists from the first tick.
 *
 * It is also the single source of truth: the hook subscribes rather than keeping
 * its own copy, so every mounted component agrees about whether the app can be
 * installed right now.
 */

/** The captured event, or null. Consumed once - the browser allows one prompt. */
let deferredPrompt = null;

/** Why the app cannot be installed, for a UI that has to explain itself. */
let unavailableReason = 'pending';

const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.warn('[PWA] install listener failed:', err);
    }
  });
};

/** True when the page is already running as an installed app. */
export const isRunningStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches ||
    window.navigator.standalone === true || // iOS Safari
    document.referrer.includes('android-app://') // Android TWA
  );
};

/**
 * Why installation is unavailable, when it is. One of:
 *   'installed'    already running as an app
 *   'unsupported'  the browser has no install prompt (Firefox, iOS Safari, …)
 *   'pending'      supported, but the browser has not offered it (yet)
 */
export const getUnavailableReason = () => {
  if (isRunningStandalone()) return 'installed';
  if (deferredPrompt) return null;
  // Chromium exposes onbeforeinstallprompt on window; its absence means the
  // browser will never offer a programmatic install, however long we wait.
  const supportsPrompt = typeof window !== 'undefined' && 'onbeforeinstallprompt' in window;
  return supportsPrompt ? unavailableReason : 'unsupported';
};

export const canInstall = () => Boolean(deferredPrompt) && !isRunningStandalone();

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Show the browser's install prompt.
 *
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
export const promptInstall = async () => {
  if (!deferredPrompt) return 'unavailable';
  const prompt = deferredPrompt;
  // Cleared BEFORE awaiting: the event is single-use, and leaving it in place
  // while the dialog is open lets a second click call prompt() again, which
  // throws and leaves the button looking broken.
  deferredPrompt = null;
  unavailableReason = 'pending';
  notify();
  try {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome !== 'accepted') {
      // Dismissed: the browser will usually offer the event again on a later
      // visit, so this is not terminal.
      unavailableReason = 'pending';
      notify();
    }
    return outcome;
  } catch (err) {
    console.warn('[PWA] install prompt failed:', err);
    return 'dismissed';
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // suppress the mini-infobar; we install from the menu
    deferredPrompt = e;
    unavailableReason = null;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    unavailableReason = 'installed';
    notify();
  });
}
